import { FC, ReactNode, useCallback } from 'react'
import classNames from 'classnames'
import classes from './classes.module.scss'
import { useTranslation } from 'react-i18next'
import Delete from 'assets/icons/delete.svg?react'
import Edit from 'assets/icons/edit.svg?react'
import Add from 'assets/icons/add.svg?react'
import Eye from 'assets/icons/eye.svg?react'
import Button, {
  AppearanceTypes,
  IconPositioningTypes,
} from 'components/molecules/Button/Button'
import { filter, map } from 'lodash'
import BaseButton from 'components/atoms/BaseButton/BaseButton'
import { ModalTypes, showModal } from 'components/organisms/modals/ModalRoot'
import { ProjectDetailModes } from 'components/organisms/ProjectDetails/ProjectDetails'
import { Price, PriceUnits } from 'types/price'
import { VolumeUnits } from 'types/assignments'
import { VolumeValue } from 'types/volumes'
import { useIsDataOwner } from 'hooks/useIsDataOwner'

interface VolumeRowProps extends VolumeValue {
  index: number
  handleDelete: (index: number, id: string) => void
  handleEdit: (index: number) => void
  disabled?: boolean
  hideActions?: boolean
}

export const apiTypeToKey = (apiType: string) => {
  switch (apiType) {
    case 'CHARACTERS':
      return PriceUnits.CharacterFee
    case 'WORDS':
      return PriceUnits.WordFee
    case 'PAGES':
      return PriceUnits.PageFee
    case 'MINUTES':
      return PriceUnits.MinuteFee
    case 'HOURS':
      return PriceUnits.HourFee
    case 'MIN_FEE':
    default:
      return PriceUnits.MinimalFee
  }
}

export const keyToApiType = (key: string): VolumeUnits => {
  switch (key) {
    case PriceUnits.CharacterFee:
      return VolumeUnits.CHARACTERS
    case PriceUnits.WordFee:
      return VolumeUnits.WORDS
    case PriceUnits.PageFee:
      return VolumeUnits.PAGES
    case PriceUnits.MinuteFee:
      return VolumeUnits.MINUTES
    case PriceUnits.HourFee:
      return VolumeUnits.HOURS
    case PriceUnits.MinimalFee:
    default:
      return VolumeUnits.MIN_FEE
  }
}

const VolumeRow: FC<VolumeRowProps> = ({
  unit_quantity,
  unit_type,
  cat_job,
  discounts,
  unit_fee,
  volume_analysis,
  index,
  handleDelete,
  handleEdit,
  disabled,
  hideActions,
  id,
}) => {
  const { t } = useTranslation()
  const onEditClick = useCallback(() => {
    handleEdit(index)
  }, [handleEdit, index])
  const onDeleteClick = useCallback(() => {
    handleDelete(index, id)
  }, [handleDelete, index, id])
  const onShowClick = useCallback(() => {
    const {
      files_names,
      repetitions,
      tm_0_49,
      tm_50_74,
      tm_75_84,
      tm_85_94,
      tm_95_99,
      tm_100,
      tm_101,
      total,
    } = volume_analysis || {}

    showModal(ModalTypes.VolumeChange, {
      isCat: true,
      mode: ProjectDetailModes.View,
      discounts,
      unit_fee,
      volume_analysis: {
        files_names,
        repetitions: repetitions || '0',
        tm_0_49: tm_0_49 || '0',
        tm_50_74: tm_50_74 || '0',
        tm_75_84: tm_75_84 || '0',
        tm_85_94: tm_85_94 || '0',
        tm_95_99: tm_95_99 || '0',
        tm_100: tm_100 || '0',
        tm_101: tm_101 || '0',
        total: total || '0',
      },
    })
  }, [discounts, unit_fee, volume_analysis])
  return (
    <div className={classes.row}>
      <span>{`${Number(unit_quantity)} ${t(
        `label.${apiTypeToKey(unit_type)}`
      )}${cat_job ? ` ${t('task.open_in_cat')}` : ''}`}</span>
      {!hideActions && !disabled && (
        <BaseButton
          onClick={onEditClick}
          className={classes.editButton}
          aria-label={t('button.edit')}
        >
          <Edit />
        </BaseButton>
      )}
      {!hideActions && !disabled && (
        <BaseButton
          onClick={onDeleteClick}
          className={classes.deleteButton}
          aria-label={t('button.delete')}
        >
          <Delete />
        </BaseButton>
      )}
      {(disabled || hideActions) && cat_job && (
        <BaseButton
          onClick={onShowClick}
          className={classes.volumeIcon}
          aria-label={t('button.view')}
        >
          <Eye />
        </BaseButton>
      )}
    </div>
  )
}

export interface AddVolumeInputProps {
  onChange: (newVolumeValues: VolumeValue[]) => void
  value?: VolumeValue[]
  name: string
  className?: string
  label?: ReactNode
  disabled?: boolean
  hidden?: boolean
  catSupported?: boolean
  vendorPrices?: Price
  vendorName?: string
  assignmentId?: string
  sub_project_id?: string
  ownerInstitutionId?: string
}

const AddVolumeInput: FC<AddVolumeInputProps> = ({
  label,
  className,
  hidden,
  onChange,
  value = [],
  catSupported,
  vendorPrices,
  vendorName,
  assignmentId,
  sub_project_id,
  ownerInstitutionId,
  disabled,
}) => {
  const { t } = useTranslation()
  const isShared = !useIsDataOwner(ownerInstitutionId)

  const handleDelete = useCallback(
    (index: number, id: string) => {
      const newVolumes = filter(
        value,
        (_, volumeIndex) => index !== volumeIndex
      )
      showModal(ModalTypes.ConfirmDeleteVolume, {
        newVolumes,
        sub_project_id,
        volumeId: id,
        callback: () => onChange(newVolumes),
        assignmentId,
      })
    },
    [value, sub_project_id, assignmentId, onChange]
  )

  const handleEdit = useCallback(
    (index: number) => {
      const matchingVolume = value[index]
      const isCat = !!matchingVolume.cat_job

      const onChangeValue = (updatedVolume: VolumeValue) => {
        onChange(
          map(value, (volume) =>
            volume.id === matchingVolume.id ? updatedVolume : volume
          )
        )
      }
      showModal(ModalTypes.VolumeChange, {
        isCat,
        vendorPrices,
        vendorName,
        onChangeValue,
        sub_project_id,
        assignmentId,
        ...matchingVolume,
      })
    },
    [value, vendorPrices, vendorName, onChange, sub_project_id, assignmentId]
  )

  const handleAdd = useCallback(() => {
    const onChangeValue = (newVolume: VolumeValue) => {
      onChange([...value, newVolume])
    }

    showModal(ModalTypes.AddVolume, {
      onChangeValue,
      assignmentId,
      sub_project_id,
      catSupported,
      vendorPrices,
      vendorName,
    })
  }, [
    assignmentId,
    sub_project_id,
    catSupported,
    vendorPrices,
    vendorName,
    onChange,
    value,
  ])
  // Might need event handler wrappers here
  if (hidden) return null
  return (
    <div className={classNames(classes.container, className)}>
      <label className={classNames(classes.label, !label && classes.hidden)}>
        {label}
      </label>
      <div className={classes.valuesContainer}>
        {map(value, (props, index) => (
          <VolumeRow
            {...{ ...props, index, handleDelete, handleEdit, disabled }}
            hideActions={isShared}
            key={index}
          />
        ))}
        <Button
          appearance={AppearanceTypes.Text}
          className={classes.addButton}
          iconPositioning={IconPositioningTypes.Left}
          disabled={disabled}
          icon={Add}
          children={t('button.add_volume')}
          onClick={handleAdd}
          hidden={isShared}
        />
      </div>
    </div>
  )
}

export default AddVolumeInput
